import { Fragment } from 'react';
import {
  companionOf,
  histogram,
  type ReportChart,
  type ReportDefinition,
  type ReportSnapshot,
} from '@forecastify/shared';
import { Tooltip, readableOn } from '@forecastify/ui';
import { useDuration, type Duration } from '../../duration';
import { useT } from '../../i18n';

/**
 * ┌───────────────────────────────────────────────────────────────────────────┐
 * │ C59 · Twenty-five shapes over one result.                                 │
 * └───────────────────────────────────────────────────────────────────────────┘
 *
 * ⚠️⚠️ **THEY ARE FAMILIES, NOT TWENTY-FIVE DRAWINGS.** Six vertical and horizontal bar variants
 * share one renderer that takes an orientation and a stacking mode; line and area share one;
 * donut and sunburst share one. Written as twenty-five independent components they would drift —
 * one would round a percentage differently, another would forget the `'none'` group — and a chart
 * picker whose options disagree about the same data is worse than half as many that do not.
 *
 * ⚠️⚠️ **THE SECOND SERIES IS THE MEASURE'S COMPANION, NOT A SECOND GROUPING.** `companionOf` pairs
 * hours with the estimate; the row already carries both. Reading a stacked bar here as "group by a
 * second field" is the mistake `charts.ts` exists to prevent — this product's report has exactly one
 * dimension.
 *
 * ⚠️ **No charting library.** These are CSS boxes and a little inline SVG, like `hours-chart.tsx`
 * and the finance dashboard. A dependency that drew them would bring its own fonts, colours and
 * tooltips to fight with the design system.
 */

const IDENTITY = [
  'var(--id-1)',
  'var(--id-2)',
  'var(--id-3)',
  'var(--id-4)',
  'var(--id-5)',
  'var(--id-6)',
  'var(--id-7)',
  'var(--id-8)',
] as const;

/** ⚠️ A stored colour where the row has one; an identity hue by POSITION where it does not. */
function hueOf(color: string | null, index: number): string {
  return color ?? IDENTITY[index % IDENTITY.length] ?? 'var(--primary)';
}

/** The companion series' own colour — deliberately grey, so the measure stays the subject. */
const COMPANION = 'color-mix(in oklch, var(--foreground) 30%, transparent)';

type Row = ReportSnapshot['rows'][number];

function valueOf(row: Row): number {
  return Number(row.value);
}

/** The companion figure for a row, or null when this measure has none. */
function companionValue(definition: ReportDefinition, row: Row): number | null {
  const companion = companionOf(definition.metric);
  if (companion === null) return null;
  if (companion === 'estimate') return row.estimate === null ? null : Number(row.estimate);
  return null;
}

/** A figure of the measure — a count, or a duration in the reader's unit (`decisions.md` DU-1). */
function format(fmt: Duration['fmt'], definition: ReportDefinition, value: number): string {
  return definition.metric === 'count' ? String(Math.round(value)) : fmt(value.toFixed(2));
}

export interface ChartProps {
  readonly definition: ReportDefinition;
  readonly snapshot: ReportSnapshot;
  /** Already-resolved group labels, so every family words the `'none'` row identically. */
  readonly labelOf: (row: Row) => string;
}

/**
 * The dispatcher.
 *
 * ⚠️ **A chart the report cannot carry falls through to the bar**, rather than rendering nothing.
 * `chartRefusal` stops the picker offering it; this is the second line of defence for a SAVED
 * report whose definition changed underneath it.
 */
export function ReportChartView(props: ChartProps & { readonly chart: ReportChart }) {
  const t = useT();
  const { chart } = props;

  /**
   * ⚠⚠ **EVERY SHAPE IS NAMED, BECAUSE NONE OF THEM IS READABLE BY A SCREEN READER.** A div of
   * coloured boxes announces as nothing at all; the figures are in the table beside it, and this
   * says which picture is being drawn. The suite asserts it — the two charts that stood here had
   * `role="img"` and an `aria-label`, and losing that in a refactor is exactly the kind of quiet
   * regression an accessibility rule cannot catch on its own.
   */
  return (
    <div role="img" aria-label={chartLabel(t, chart)}>
      {inner(props)}
    </div>
  );
}

function inner(props: ChartProps & { readonly chart: ReportChart }) {
  const { chart } = props;

  if (chart === 'metric') return <BigNumber {...props} />;
  if (chart === 'table') return <NoChart />;
  if (chart === 'gauge') return <Gauge {...props} />;
  if (chart === 'donut' || chart === 'sunburst')
    return <Donut {...props} ringed={chart === 'sunburst'} />;
  if (chart === 'breakdown') return <Breakdown {...props} />;
  if (chart === 'histogram') return <Histogram {...props} />;
  if (chart === 'heatmap') return <Matrix {...props} />;
  if (chart === 'calendar') return <Calendar {...props} />;
  if (chart === 'treemap') return <Treemap {...props} />;
  if (chart === 'waterfall') return <Waterfall {...props} />;
  if (chart === 'radar') return <Radar {...props} />;
  if (chart === 'scatter' || chart === 'bubble')
    return <Scatter {...props} bubbles={chart === 'bubble'} />;
  if (chart === 'line' || chart === 'area')
    return <LineChart {...props} filled={chart === 'area'} />;
  if (chart === 'barline') return <BarLine {...props} />;
  if (chart === 'listmetric' || chart === 'listdim') {
    return <RankedList {...props} byName={chart === 'listdim'} />;
  }
  if (chart === 'hbar' || chart === 'hstack' || chart === 'hgroup') {
    return (
      <Bars
        {...props}
        horizontal
        mode={chart === 'hstack' ? 'stack' : chart === 'hgroup' ? 'group' : 'single'}
      />
    );
  }
  return (
    <Bars
      {...props}
      horizontal={false}
      mode={chart === 'vstack' ? 'stack' : chart === 'vgroup' ? 'group' : 'single'}
    />
  );
}

/* ── bars: six of the twenty-five ──────────────────────────────────────────── */

/**
 * ⚠️⚠️ **SCALED TO THE TALLEST, NOT TO THE TOTAL** — `hours-chart.tsx` and the finance dashboard
 * record the same rule. Bars scaled to the total are all short and the shape, which is the only
 * thing a bar chart is for, becomes unreadable.
 *
 * ⚠️ **`stack` and `group` differ in what they let you compare.** Stacked answers *"how big
 * altogether"*; grouped answers *"which of the two is bigger"*. Offering only one would silently
 * pick a question for the reader.
 */
function Bars({
  definition,
  snapshot,
  labelOf,
  horizontal,
  mode,
}: ChartProps & { readonly horizontal: boolean; readonly mode: 'single' | 'stack' | 'group' }) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const pairs = rows.map((row) => ({
    row,
    own: valueOf(row),
    companion: mode === 'single' ? null : companionValue(definition, row),
  }));

  /**
   * ⚠️⚠️ **`barPercent` IS THE SERVER'S AND THE CLIENT DOES NOT RE-DERIVE IT.** "Scaled to the
   * tallest" is a rule, and a rule computed in two places is a rule that will disagree with itself
   * — the snapshot carries `barPercent` and `sharePercent` precisely so the chart and the *Arány*
   * column can use different denominators without either inventing one.
   *
   * ⚠️ The companion modes are the exception, and only because the denominator genuinely changes:
   * a stack is measured against the tallest COMBINED bar, which the server knows nothing about.
   */
  const ceiling = Math.max(
    1,
    ...pairs.map((pair) =>
      mode === 'stack' ? pair.own + (pair.companion ?? 0) : Math.max(pair.own, pair.companion ?? 0),
    ),
  );
  const share = (pair: (typeof pairs)[number], value: number): number =>
    mode === 'single' ? pair.row.barPercent : (value / ceiling) * 100;

  if (horizontal) {
    return (
      <div className="flex flex-col gap-2 p-4">
        {pairs.map((pair, index) => (
          <div
            key={pair.row.key}
            className="grid grid-cols-[minmax(0,140px)_minmax(0,1fr)_auto] items-center gap-2"
          >
            <span className="text-muted-foreground truncate text-[11.5px]">
              {labelOf(pair.row)}
            </span>
            <span className="bg-muted/50 relative flex h-4 overflow-hidden rounded-sm">
              <span
                className="h-full"
                style={{
                  width: `${String(share(pair, pair.own))}%`,
                  background: hueOf(pair.row.color, index),
                }}
              />
              {pair.companion === null ? null : (
                <span
                  className={`h-full ${mode === 'group' ? 'opacity-70' : ''}`}
                  style={{
                    width: `${String((pair.companion / ceiling) * 100)}%`,
                    background: COMPANION,
                    /* ⚠️ Grouped draws the companion BESIDE, stacked draws it after — same two
                       numbers, different question. */
                    marginLeft: mode === 'group' ? 2 : 0,
                  }}
                />
              )}
            </span>
            <span className="text-right text-[11.5px] tabular-nums">
              {format(fmt, definition, pair.own)}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="flex h-[268px] items-end gap-2 p-4">
      {pairs.map((pair, index) => (
        <div key={pair.row.key} className="flex h-full min-w-0 flex-1 flex-col justify-end gap-1">
          <span className="text-center text-[11px] tabular-nums">
            {format(fmt, definition, pair.own)}
          </span>
          {/* ⚠️⚠️ 30 Oct 2026 · **A TRACK WITH A HEIGHT, AND THE BARS MEASURED AGAINST IT.** The bars
              were a percentage of a wrapper that had no height of its own, so every percentage
              resolved to nothing and every bar fell to its 3 px floor — a row of flat lines,
              whatever the figures (UAT). The track fills the column between the figure and the
              label; the bars stand on its floor. */}
          <span className="relative min-h-0 w-full flex-1">
            {mode === 'stack' ? (
              <span
                className="absolute inset-x-0 bottom-0 flex min-h-[3px] flex-col-reverse"
                style={{
                  height: `${String(((pair.own + (pair.companion ?? 0)) / ceiling) * 100)}%`,
                }}
              >
                <span
                  className="min-h-[3px] w-full rounded-t-[3px]"
                  style={{
                    flex: `${String(pair.own)} 1 0`,
                    background: hueOf(pair.row.color, index),
                  }}
                />
                {pair.companion === null ? null : (
                  <span
                    className="min-h-[3px] w-full rounded-t-[3px]"
                    style={{ flex: `${String(pair.companion)} 1 0`, background: COMPANION }}
                  />
                )}
              </span>
            ) : (
              /* ⚠️⚠️ **PERCENTAGES OF THE TRACK, NEVER PIXELS.** A pixel height computed from a
                 constant is a bar that stops responding the day the container changes — and the
                 suite asserts the tallest bar is exactly `100%`, which is the property "scaled to
                 the tallest" actually means. */
              <span className="absolute inset-0 flex items-end gap-[2px]">
                <span
                  className="min-h-[3px] min-w-0 flex-1 rounded-t-[3px]"
                  style={{
                    height: `${String(share(pair, pair.own))}%`,
                    background: hueOf(pair.row.color, index),
                  }}
                />
                {pair.companion === null ? null : (
                  <span
                    className="min-h-[3px] min-w-0 flex-1 rounded-t-[3px]"
                    style={{
                      height: `${String((pair.companion / ceiling) * 100)}%`,
                      background: COMPANION,
                    }}
                  />
                )}
              </span>
            )}
          </span>
          <span className="text-muted-foreground truncate text-center text-[11px]">
            {labelOf(pair.row)}
          </span>
        </div>
      ))}
    </div>
  );
}

/* ── line and area ─────────────────────────────────────────────────────────── */

function LineChart({
  definition,
  snapshot,
  labelOf,
  filled,
}: ChartProps & { readonly filled: boolean }) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const ceiling = Math.max(1, ...rows.map(valueOf));
  const step = rows.length <= 1 ? 100 : 100 / (rows.length - 1);
  const points = rows
    .map((row, index) => `${String(index * step)},${String(100 - (valueOf(row) / ceiling) * 100)}`)
    .join(' ');

  return (
    <div className="p-4">
      <div className="relative h-[240px]">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="size-full" aria-hidden>
          {filled ? (
            <polygon
              points={`0,100 ${points} 100,100`}
              fill="color-mix(in oklch, var(--primary) 22%, transparent)"
            />
          ) : null}
          <polyline
            points={points}
            fill="none"
            stroke="var(--primary)"
            strokeWidth="0.7"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      <div className="mt-1 flex">
        {rows.map((row) => (
          <span
            key={row.key}
            className="text-muted-foreground min-w-0 flex-1 truncate text-center text-[11px]"
          >
            {labelOf(row)}
          </span>
        ))}
      </div>
      <p className="text-muted-foreground mt-1 text-[11px]">
        {format(fmt, definition, Math.max(...rows.map(valueOf)))}
      </p>
    </div>
  );
}

/**
 * ⚠️ **Bars and a line over the SAME two series** — the measure as bars for magnitude, the
 * companion as a line for trend. It is the one shape that reads both at once, which is why the
 * mockup files it under *Kiemelt*.
 */
function BarLine({ definition, snapshot, labelOf }: ChartProps) {
  const rows = snapshot.rows;
  const pairs = rows.map((row) => ({
    row,
    own: valueOf(row),
    companion: companionValue(definition, row) ?? 0,
  }));
  const ceiling = Math.max(1, ...pairs.map((pair) => Math.max(pair.own, pair.companion)));
  const step = pairs.length <= 1 ? 100 : 100 / (pairs.length - 1);
  const line = pairs
    .map(
      (pair, index) => `${String(index * step)},${String(100 - (pair.companion / ceiling) * 100)}`,
    )
    .join(' ');

  return (
    <div className="p-4">
      <div className="relative h-[240px]">
        <div className="absolute inset-0 flex items-end gap-2">
          {pairs.map((pair, index) => (
            <span
              key={pair.row.key}
              className="min-h-[3px] min-w-0 flex-1 rounded-t-[3px]"
              style={{
                height: `${String((pair.own / ceiling) * 100)}%`,
                background: hueOf(pair.row.color, index),
              }}
            />
          ))}
        </div>
        <svg
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
          className="absolute inset-0 size-full"
          aria-hidden
        >
          <polyline
            points={line}
            fill="none"
            stroke="var(--destructive)"
            strokeWidth="0.7"
            vectorEffect="non-scaling-stroke"
          />
        </svg>
      </div>
      <div className="mt-1 flex">
        {pairs.map((pair) => (
          <span
            key={pair.row.key}
            className="text-muted-foreground min-w-0 flex-1 truncate text-center text-[11px]"
          >
            {labelOf(pair.row)}
          </span>
        ))}
      </div>
    </div>
  );
}

/* ── circular ──────────────────────────────────────────────────────────────── */

/**
 * ⚠️ **One renderer for the donut and the sunburst.** A sunburst over a single dimension is a donut
 * with a second ring for the companion — the mockup's own reading, and the reason it disables the
 * sunburst when the measure has no companion.
 */
function Donut({
  definition,
  snapshot,
  labelOf,
  ringed,
}: ChartProps & { readonly ringed: boolean }) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const total = rows.reduce((sum, row) => sum + valueOf(row), 0) || 1;

  let sweep = 0;
  const arcs = rows.map((row, index) => {
    const from = sweep;
    sweep += (valueOf(row) / total) * 360;
    return { row, index, from, to: sweep };
  });

  const gradient = arcs
    .map((arc) => `${hueOf(arc.row.color, arc.index)} ${String(arc.from)}deg ${String(arc.to)}deg`)
    .join(', ');

  return (
    <div className="flex flex-wrap items-center gap-6 p-5">
      <div
        className="relative size-[190px] shrink-0 rounded-full"
        style={{ background: `conic-gradient(${gradient})` }}
        aria-hidden
      >
        {ringed ? (
          /* ⚠️ The inner ring is the COMPANION's share of the same total, which is what makes the
             two rings comparable rather than decorative. */
          <div
            className="absolute inset-[26px] rounded-full"
            style={{
              background: `conic-gradient(${arcs
                .map((arc) => {
                  const companion = companionValue(definition, arc.row) ?? 0;
                  const span = (companion / total) * 360;
                  return `${COMPANION} ${String(arc.from)}deg ${String(arc.from + span)}deg, transparent ${String(arc.from + span)}deg ${String(arc.to)}deg`;
                })
                .join(', ')})`,
            }}
          />
        ) : null}
        <div className="bg-card absolute inset-[52px] grid place-items-center rounded-full">
          <span className="text-[13px] font-medium tabular-nums">
            {format(fmt, definition, total)}
          </span>
        </div>
      </div>

      <ul className="flex min-w-0 flex-1 flex-col gap-1.5">
        {arcs.map((arc) => (
          <li key={arc.row.key} className="flex items-center gap-2 text-[12px]">
            <span
              className="size-2.5 shrink-0 rounded-[3px]"
              style={{ background: hueOf(arc.row.color, arc.index) }}
            />
            <span className="min-w-0 flex-1 truncate">{labelOf(arc.row)}</span>
            {/* 30 Oct 2026 · fixed cells, so the share and the value line up row to row. */}
            <span className="text-muted-foreground w-[40px] shrink-0 text-right tabular-nums">{`${String(arc.row.sharePercent)}%`}</span>
            <span className="w-[104px] shrink-0 truncate text-right tabular-nums">
              {format(fmt, definition, valueOf(arc.row))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/** One bar, cut into shares. ⚠️ The whole point is proportion, so it is scaled to the TOTAL. */
function Breakdown({ definition, snapshot, labelOf }: ChartProps) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const total = rows.reduce((sum, row) => sum + valueOf(row), 0) || 1;

  return (
    <div className="p-5">
      <div className="flex h-7 overflow-hidden rounded-md">
        {rows.map((row, index) => (
          <Tooltip key={row.key} asChild content={labelOf(row)}>
            <span
              className="h-full"
              style={{
                width: `${String((valueOf(row) / total) * 100)}%`,
                background: hueOf(row.color, index),
              }}
            />
          </Tooltip>
        ))}
      </div>
      <ul className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {rows.map((row, index) => (
          <li key={row.key} className="flex items-center gap-1.5 text-[11.5px]">
            <span
              className="size-2 shrink-0 rounded-[2px]"
              style={{ background: hueOf(row.color, index) }}
            />
            <span className="truncate">{labelOf(row)}</span>
            <span className="text-muted-foreground tabular-nums">
              {format(fmt, definition, valueOf(row))}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/* ── the single-figure shapes ──────────────────────────────────────────────── */

function BigNumber({ definition, snapshot }: ChartProps) {
  const { fmt } = useDuration();
  const t = useT();
  return (
    <div className="flex flex-col items-center gap-1 px-6 py-14">
      <span className="text-[40px] leading-none font-medium tabular-nums">
        {format(fmt, definition, Number(snapshot.total))}
      </span>
      <span className="text-muted-foreground text-[12px]">
        {t('report.chart.acrossGroups', { count: snapshot.rows.length })}
      </span>
    </div>
  );
}

/**
 * ⚠️ **Against the biggest group, because a report has no target field.** The mockup's gauge falls
 * back to exactly this when no target is set, and it is the honest reading: *"the leader is this
 * far ahead"* rather than a percentage of a goal nobody entered.
 */
function Gauge({ snapshot, labelOf }: ChartProps) {
  const t = useT();
  const rows = snapshot.rows;
  const leader = rows[0];
  const total = Number(snapshot.total) || 1;
  const share = leader === undefined ? 0 : Math.round((valueOf(leader) / total) * 100);

  return (
    <div className="flex flex-col items-center gap-3 px-6 py-10">
      <div className="relative h-[110px] w-[220px] overflow-hidden">
        <div className="bg-muted absolute inset-x-0 top-0 h-[220px] rounded-full" />
        <div
          className="absolute inset-x-0 top-0 h-[220px] rounded-full"
          style={{
            background: `conic-gradient(from 270deg, var(--primary) 0deg ${String(share * 1.8)}deg, transparent ${String(share * 1.8)}deg)`,
          }}
        />
        <div className="bg-card absolute inset-x-[34px] top-[34px] h-[152px] rounded-full" />
        <span className="absolute inset-x-0 bottom-1 text-center text-[24px] font-medium tabular-nums">
          {`${String(share)}%`}
        </span>
      </div>
      <span className="text-muted-foreground text-[12px]">
        {leader === undefined
          ? t('report.empty.title')
          : t('report.chart.leaderShare', { name: labelOf(leader) })}
      </span>
    </div>
  );
}

/** ⚠️ Ranked by the metric, or alphabetically — the two lists the mockup distinguishes. */
function RankedList({
  definition,
  snapshot,
  labelOf,
  byName,
}: ChartProps & { readonly byName: boolean }) {
  const { fmt } = useDuration();
  const rows = [...snapshot.rows];
  if (byName) rows.sort((one, other) => labelOf(one).localeCompare(labelOf(other)));

  return (
    <ul className="flex flex-col">
      {rows.map((row, index) => (
        <li
          key={row.key}
          className="flex items-center gap-3 border-b px-4 py-2.5 text-[12.5px] last:border-0"
        >
          <span className="text-muted-foreground w-5 shrink-0 text-right tabular-nums">
            {String(index + 1)}
          </span>
          <span
            className="size-2.5 shrink-0 rounded-[3px]"
            style={{ background: hueOf(row.color, index) }}
          />
          <span className="min-w-0 flex-1 truncate">{labelOf(row)}</span>
          <span className="bg-muted hidden h-1.5 w-24 overflow-hidden rounded-full sm:block">
            <span
              className="bg-primary block h-full"
              style={{ width: `${String(row.barPercent)}%` }}
            />
          </span>
          {/* 30 Oct 2026 · a fixed cell — a wider value pushed the bars out of line. */}
          <span className="w-[104px] shrink-0 truncate text-right tabular-nums">
            {format(fmt, definition, valueOf(row))}
          </span>
        </li>
      ))}
    </ul>
  );
}

/* ── the shapes that read the extra axes ───────────────────────────────────── */

/**
 * ⚠️⚠️ **THE GROUPS, BINNED — NOT THE RECORDS.** *"How many projects booked between 40 and 80
 * hours"* is the question a grouped report can answer. *"How many worklogs were an hour"* is a
 * different report over rows this one has already summed away, and drawing it from these rows would
 * answer the second question with the first one's data.
 */
function Histogram({ definition, snapshot }: ChartProps) {
  const { fmt } = useDuration();
  const t = useT();
  const bins = histogram(snapshot.rows.map(valueOf));
  const tallest = Math.max(1, ...bins.map((bin) => bin.count));

  return (
    <div className="p-4">
      <div className="flex h-[200px] items-end gap-2">
        {bins.map((bin) => (
          <div key={bin.from} className="flex h-full min-w-0 flex-1 flex-col justify-end gap-1">
            <span className="text-center text-[11px] tabular-nums">{String(bin.count)}</span>
            {/* 30 Oct 2026 · on a track with a height — `Bars` says why. */}
            <span className="relative min-h-0 w-full flex-1">
              <span
                className="bg-primary absolute inset-x-0 bottom-0 min-h-[3px] rounded-t-[3px]"
                style={{ height: `${String((bin.count / tallest) * 100)}%` }}
              />
            </span>
            <span className="text-muted-foreground truncate text-center text-[10.5px] tabular-nums">
              {`${format(fmt, definition, bin.from)}–${format(fmt, definition, bin.to)}`}
            </span>
          </div>
        ))}
      </div>
      <p className="text-muted-foreground mt-2 text-[11px]">{t('report.chart.histogramNote')}</p>
    </div>
  );
}

/** Group × month, from the matrix the run computed alongside the rows. */
function Matrix({ definition, snapshot, labelOf }: ChartProps) {
  const { fmt } = useDuration();
  const t = useT();
  const { months, rows } = snapshot.matrix;
  if (months.length === 0) {
    return <Unavailable note={t('report.chart.needsMatrix')} />;
  }

  const ceiling = Math.max(1, ...rows.flatMap((row) => row.values.map((value) => Number(value))));

  return (
    <div className="overflow-x-auto p-4">
      <div
        className="grid min-w-[520px] gap-1"
        style={{ gridTemplateColumns: `minmax(0,150px) repeat(${String(months.length)}, 1fr)` }}
      >
        <span />
        {months.map((month) => (
          <span
            key={month}
            className="text-muted-foreground text-center text-[10.5px] tabular-nums"
          >
            {month}
          </span>
        ))}
        {rows.map((row) => {
          const label = snapshot.rows.find((one) => one.key === row.key);
          /* ⚠️ The fragment is the list item, so the KEY goes on it — on its children it keyed
             nothing, and React warned on every heatmap (3 Nov 2026). */
          return (
            <Fragment key={row.key}>
              <span className="truncate text-[11.5px]">
                {label === undefined ? row.key : labelOf(label)}
              </span>
              {row.values.map((value, index) => (
                <span
                  key={`${row.key}-${months[index] ?? String(index)}`}
                  className="grid h-7 place-items-center rounded-[3px] text-[10.5px] tabular-nums"
                  style={{
                    /* ⚠️ Opacity carries the magnitude; an empty month is a faint cell rather than
                       a blank, so the grid stays a grid. */
                    background: `color-mix(in oklch, var(--primary) ${String(
                      Math.round((Number(value) / ceiling) * 100),
                    )}%, transparent)`,
                  }}
                >
                  {Number(value) === 0 ? '' : format(fmt, definition, Number(value))}
                </span>
              ))}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

/** A year of days, from the per-day series. ⚠️ Weeks run down, days across — the GitHub shape. */
function Calendar({ definition, snapshot }: ChartProps) {
  const { fmt } = useDuration();
  const t = useT();
  const days = snapshot.days;
  if (days.length === 0) return <Unavailable note={t('report.chart.needsDays')} />;

  const ceiling = Math.max(1, ...days.map((day) => Number(day.value)));
  const byDate = new Map(days.map((day) => [day.on, Number(day.value)]));

  /* ⚠️ From the first day with data to the last — a fixed year window would draw mostly emptiness
     for a report whose period is one month. */
  const first = days[0]?.on ?? '';
  const last = days[days.length - 1]?.on ?? '';
  const cells: { on: string; value: number }[] = [];
  const cursor = new Date(`${first}T00:00:00Z`);
  const end = new Date(`${last}T00:00:00Z`);
  let guard = 0;
  while (cursor <= end && guard++ < 800) {
    const on = cursor.toISOString().slice(0, 10);
    cells.push({ on, value: byDate.get(on) ?? 0 });
    cursor.setUTCDate(cursor.getUTCDate() + 1);
  }

  return (
    <div className="overflow-x-auto p-4">
      <div className="grid grid-flow-col grid-rows-7 gap-[3px]">
        {cells.map((cell) => (
          <Tooltip
            key={cell.on}
            asChild
            content={`${cell.on} · ${format(fmt, definition, cell.value)}`}
          >
            <span
              className="size-3 rounded-[2px]"
              style={{
                background:
                  cell.value === 0
                    ? 'var(--muted)'
                    : `color-mix(in oklch, var(--primary) ${String(
                        Math.max(12, Math.round((cell.value / ceiling) * 100)),
                      )}%, transparent)`,
              }}
            />
          </Tooltip>
        ))}
      </div>
      <p className="text-muted-foreground mt-2 text-[11px]">
        {t('report.chart.calendarNote', { days: String(cells.length) })}
      </p>
    </div>
  );
}

/* ── the remaining featured shapes ─────────────────────────────────────────── */

/** ⚠️ Area carries the value, so the biggest group is visibly biggest without reading a number. */
function Treemap({ definition, snapshot, labelOf }: ChartProps) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const total = rows.reduce((sum, row) => sum + valueOf(row), 0) || 1;

  return (
    <div className="flex h-[260px] flex-wrap content-start gap-1 p-3">
      {rows.map((row, index) => {
        const share = valueOf(row) / total;
        return (
          <div
            key={row.key}
            className="flex min-w-[80px] flex-col justify-between overflow-hidden rounded-md p-2"
            style={{
              /* ⚠️ A square-ish tile whose AREA is the share — `sqrt` is what keeps a 4× value four
                 times the area rather than four times the width. */
              flexGrow: Math.max(1, Math.round(share * 100)),
              flexBasis: `${String(Math.max(18, Math.sqrt(share) * 100))}%`,
              background: hueOf(row.color, index),
              /* ⚠️ Measured against a stored colour (`readableOn`); on an identity hue, its ink. */
              color: row.color === null ? 'var(--on-id)' : readableOn(row.color),
            }}
          >
            <span className="truncate text-[11.5px] font-medium">{labelOf(row)}</span>
            <span className="text-[11px] tabular-nums opacity-90">
              {format(fmt, definition, valueOf(row))}
            </span>
          </div>
        );
      })}
    </div>
  );
}

/** ⚠️ Each group as a step off the running total — where the figure came from, not just its size. */
function Waterfall({ definition, snapshot, labelOf }: ChartProps) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const total = rows.reduce((sum, row) => sum + valueOf(row), 0) || 1;

  let running = 0;
  const steps = rows.map((row, index) => {
    const from = running;
    running += valueOf(row);
    return { row, index, from, to: running };
  });

  return (
    <div className="p-4">
      <div className="flex h-[240px] items-end gap-2">
        {steps.map((step) => (
          <div key={step.row.key} className="flex h-full min-w-0 flex-1 flex-col justify-end">
            <span className="text-center text-[11px] tabular-nums">
              {format(fmt, definition, valueOf(step.row))}
            </span>
            {/* 30 Oct 2026 · on a track with a height, each step standing where the running total
                left off. It was a percentage HEIGHT plus a percentage MARGIN — and a percentage
                margin is a share of the WIDTH, so the steps floated at the wrong heights. */}
            <span className="relative mt-1 min-h-0 w-full flex-1">
              <span
                className="absolute inset-x-0 min-h-[3px] rounded-[3px]"
                style={{
                  bottom: `${String((step.from / total) * 100)}%`,
                  height: `${String(((step.to - step.from) / total) * 100)}%`,
                  background: hueOf(step.row.color, step.index),
                }}
              />
            </span>
            <span className="text-muted-foreground mt-1 truncate text-center text-[11px]">
              {labelOf(step.row)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** ⚠️ The measure against its companion, one spoke per group. */
function Radar({ definition, snapshot, labelOf }: ChartProps) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const ceiling = Math.max(
    1,
    ...rows.map((row) => Math.max(valueOf(row), companionValue(definition, row) ?? 0)),
  );

  const polygon = (pick: (row: Row) => number): string =>
    rows
      .map((row, index) => {
        const angle = (index / rows.length) * Math.PI * 2 - Math.PI / 2;
        const radius = (pick(row) / ceiling) * 46;
        return `${String(50 + Math.cos(angle) * radius)},${String(50 + Math.sin(angle) * radius)}`;
      })
      .join(' ');

  return (
    <div className="flex flex-wrap items-center gap-6 p-5">
      <svg viewBox="0 0 100 100" className="size-[220px] shrink-0" aria-hidden>
        {[46, 34, 23, 11].map((radius) => (
          <circle
            key={radius}
            cx="50"
            cy="50"
            r={radius}
            fill="none"
            stroke="var(--border)"
            strokeWidth="0.4"
          />
        ))}
        <polygon points={polygon((row) => companionValue(definition, row) ?? 0)} fill={COMPANION} />
        <polygon
          points={polygon(valueOf)}
          fill="color-mix(in oklch, var(--primary) 35%, transparent)"
          stroke="var(--primary)"
          strokeWidth="0.6"
        />
      </svg>
      <ul className="flex min-w-0 flex-1 flex-col gap-1">
        {rows.map((row) => (
          <li key={row.key} className="flex items-center gap-2 text-[11.5px]">
            <span className="min-w-0 flex-1 truncate">{labelOf(row)}</span>
            <span className="tabular-nums">{format(fmt, definition, valueOf(row))}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}

/**
 * ⚠️ **The measure against the group's COUNT** — how much, against how many. A bubble adds the
 * share as the third channel, which is the only difference between the two charts.
 */
function Scatter({
  definition,
  snapshot,
  labelOf,
  bubbles,
}: ChartProps & { readonly bubbles: boolean }) {
  const { fmt } = useDuration();
  const rows = snapshot.rows;
  const highest = Math.max(1, ...rows.map(valueOf));
  const widest = Math.max(1, ...rows.map((row) => row.count));

  return (
    <div className="p-4">
      <div className="bg-muted/20 relative h-[240px] rounded-md border">
        {rows.map((row, index) => (
          <Tooltip
            key={row.key}
            asChild
            content={`${labelOf(row)} · ${format(fmt, definition, valueOf(row))}`}
          >
            <span
              className="absolute -translate-x-1/2 translate-y-1/2 rounded-full"
              style={{
                left: `${String((row.count / widest) * 88 + 6)}%`,
                bottom: `${String((valueOf(row) / highest) * 84 + 6)}%`,
                width: bubbles ? `${String(8 + (row.sharePercent / 100) * 36)}px` : '10px',
                height: bubbles ? `${String(8 + (row.sharePercent / 100) * 36)}px` : '10px',
                background: hueOf(row.color, index),
                opacity: bubbles ? 0.75 : 1,
              }}
            />
          </Tooltip>
        ))}
      </div>
      <div className="text-muted-foreground mt-1.5 flex justify-between text-[11px]">
        <span>{fmt(0)}</span>
        <span>{String(widest)}</span>
      </div>
    </div>
  );
}

/** ⚠️ The table is the chart — `showTable` renders it, so this slot says so rather than blanking. */
function NoChart() {
  const t = useT();
  return (
    <p className="text-muted-foreground px-6 py-10 text-center text-[12.5px]">
      {t('report.chart.tableOnly')}
    </p>
  );
}

function Unavailable({ note }: { readonly note: string }) {
  return <p className="text-muted-foreground px-6 py-10 text-center text-[12.5px]">{note}</p>;
}

/**
 * ⚠️ **One literal key per chart.** A computed `t(`report.chart.${id}`)` is invisible to the
 * catalogue check, which is how a name goes missing in one language and nothing notices.
 */
export function chartLabel(t: ReturnType<typeof useT>, chart: ReportChart): string {
  switch (chart) {
    case 'treemap':
      return t('report.chart.treemap');
    case 'waterfall':
      return t('report.chart.waterfall');
    case 'calendar':
      return t('report.chart.calendar');
    case 'sunburst':
      return t('report.chart.sunburst');
    case 'bubble':
      return t('report.chart.bubble');
    case 'scatter':
      return t('report.chart.scatter');
    case 'radar':
      return t('report.chart.radar');
    case 'barline':
      return t('report.chart.barline');
    case 'line':
      return t('report.chart.line');
    case 'area':
      return t('report.chart.area');
    case 'vbar':
      return t('report.chart.vbar');
    case 'vstack':
      return t('report.chart.vstack');
    case 'vgroup':
      return t('report.chart.vgroup');
    case 'hbar':
      return t('report.chart.hbar');
    case 'hstack':
      return t('report.chart.hstack');
    case 'hgroup':
      return t('report.chart.hgroup');
    case 'listmetric':
      return t('report.chart.listmetric');
    case 'listdim':
      return t('report.chart.listdim');
    case 'metric':
      return t('report.chart.metric');
    case 'table':
      return t('report.chart.tableChart');
    case 'gauge':
      return t('report.chart.gauge');
    case 'donut':
      return t('report.chart.donut');
    case 'breakdown':
      return t('report.chart.breakdown');
    case 'histogram':
      return t('report.chart.histogram');
    default:
      return t('report.chart.heatmap');
  }
}

/** Why the picker will not offer a chart — one sentence, naming the thing that is missing. */
export function refusalNote(
  t: ReturnType<typeof useT>,
  refusal: 'companion' | 'days' | 'matrix',
): string {
  if (refusal === 'companion') return t('report.chart.needsCompanion');
  if (refusal === 'days') return t('report.chart.needsDays');
  return t('report.chart.needsMatrix');
}
